import * as mobx from 'mobx';
import * as mobxReact from 'mobx-react';
import * as React from 'react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import { EventAccess, EventStatus } from '../../../../api/events.dao';
import { Permission } from '../../../../api/roles.dao';
import {
    addText,
    cancelText,
    COORDINATORS_TEXT,
    END_DATE_TEXT,
    END_TIME_TEXT,
    EVENT_PRIVATE_DESCRIPTION_TEXT,
    EVENT_PUBLIC_DESCRIPTION_TEXT,
    EVENT_THUMBNAIL_IMAGE_TEXT,
    EVENT_TITLE_FOR_PASSPORT_TEXT,
    EXTERNAL_ID_TEXT,
    MIN_VALUE_REQUIRED_ERROR_TEXT,
    MORE_BUTTON_TEXT,
    REMOVE_TEXT,
    STAFFING_END_DATE_TEXT,
    STAFFING_START_DATE_TEXT,
    START_DATE_TEXT,
    START_TIME_TEXT,
    submitText
} from '../../../../constants/common-labels.constants';
import { EnablementStatus } from '../../../../constants/common.constants';
import { getFormattedDeliveryMemberRoles } from '../../../../constants/entity-role-with-intl';
import { clientDepartmentText, eventBusinessLines, eventRegions, eventTypes, noClientDepartmentText } from '../../../../constants/EventValues';
import { BILLING_LOCKED_NOTICE_TEXT } from '../../_manage-event/shared/salesforce-order/salesforce-order.texts';
import { ONLY_SPACES_REG_EXP } from '../../../../constants/regex.constants';
import { DateFnsFormats, IDateTimeFormatter, TimeFormats } from '../../../../infrastructure/date-formatter';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { CommonPopupBodyFooterLayout } from '../../../global/common-popup/common-popup-body-footer-layout';
import DefaultButton from '../../../global/default-button/default-button';
import { DefaultDatePicker } from '../../../global/default-datepicker/default-datepicker';
import { FormField } from '../../../global/form/form-field/form-field';
import { MultiSelectButton } from '../../../global/multi-select/multi-select-button';
import OENotification, { ResponseCode } from '../../../global/oe-notification/oe-notification';
import { OEToggle } from '../../../global/oe-toggle/oe-toggle';
import OptionsButton from '../../../global/options-button/options-button';
import { RichTextEditor } from '../../../global/rich-text-editor';
import { SelectTimezones } from '../../../global/select-timezone/select-timezones';
import { DefaultCloseSubWindowButton } from '../../../global/sub-window/common/common-close-sub-window-buttons';
import Text from '../../../global/text/text';
import { TimeInput } from '../../../global/time-input/time-input';
import { EventLinks } from '../../_manage-event/shared/event-links/event-links.container';
import { EventProgressBar } from '../../_manage-event/shared/progress-bar/event-progress-bar';
import { ViewBlueprintDetailsSection } from '../../_manage-event/shared/view-blueprint-details-section/view-blueprint-details-section';
import { DeliveryTeam } from './delivery-team/delivery-team';
import './event-form-presentation.scss';
import { EventFormVM } from './event-form.viewmodel';
import { EventRegistrationToken } from './event-registration-token/event-registration-token';
import { EventAttributeTypesValues } from '../../../../api/attributes.dao';
import OECheckbox from '../../../global/oe-checkbox/oe-checkbox';
import { UploadImageS3Presentation } from '../../_manage-event/shared/upload-image/upload-image-to-s3.presentation';

interface IProps {
    vm: EventFormVM;
    intl: IntlShape;
}


const EventFormPresentation = injectIntl(mobxReact.observer((props: IProps) => {
    const { vm, intl } = props;

    const deletePermission = hasPermission(Permission.DELETE_EVENT);
    const editPermission = hasPermission(Permission.EDIT_EVENT);
    const formData = props.vm.getFormFields();
    const isEdit = !!vm.getEventId();
    const isEventCancelled = formData.eventStatus === EventStatus.CANCELLED;
    const isDisabled = formData.isValidationErrorExist();
    const eventOptionsButton = [
        deletePermission && isEdit && {
            label: intl.formatMessage({
                id: `oe.event.deleteEvent`,
                description: 'Label text',
                defaultMessage: 'Delete Event',
            }),
            action: vm.deleteEvent,
            danger: true
        },
        editPermission && isEdit && {
            label: isEventCancelled
                ? intl.formatMessage({
                    id: `oe.event.reinstateEvent`,
                    description: 'Label text',
                    defaultMessage: 'Reinstate Event',
                })
                : intl.formatMessage({
                    id: `oe.event.cancelEvent`,
                    description: 'Label text',
                    defaultMessage: 'Cancel Event',
                }),
            action: vm.cancelEvent,
            danger: true
        },

    ].filter(Boolean);

    const eventAccess = [
        {
            value: EventAccess.Restricted,
            label: intl.formatMessage({
                id: 'oe.eventAccess.Restricted',
                description: 'Label text',
                defaultMessage: 'Restricted',
            })
        },
        {
            value: EventAccess.Open,
            label: intl.formatMessage({
                id: 'oe.eventAccess.open',
                description: 'Label text',
                defaultMessage: 'Open',
            })
        }
    ];

    const eventTypesLabel = eventTypes(intl);

    const removeOnlySpacesFromField = mobx.action((value: string, key: string) => {
        if (!(value.replace(ONLY_SPACES_REG_EXP, '').length)) {
            formData[key] = '';
        }
    });

    const isInternalEventLinksPermission = hasPermission(Permission.VIEW_INTERNAL_EVENT_LINKS);

    const dateTimeFormatter = IoC.Container.get(IDateTimeFormatter);
    const dateFormat = dateTimeFormatter.getLocalTimeFormat() === TimeFormats.Day24Hours
        ? DateFnsFormats.StringifiedShortFullDateDay24Hours
        : DateFnsFormats.StringifiedShortFullDateDay12Hours;

    const formattedDeliveryMemberRoles = getFormattedDeliveryMemberRoles(intl);

    return (
        <CommonPopupBodyFooterLayout
            body={(
                <form className='oe-form event-form'>
                    <div className='oe-form__body popup-form'>
                        {isEventCancelled && <OENotification popupProps={{
                            message: <FormattedMessage
                                id='oe.event.eventCancelled'
                                description='Event status notification'
                                defaultMessage='Event Cancelled'
                            />,
                            statusCode: ResponseCode.warning,
                            withFade: false
                        }} />}
                        <FormField
                            className='m-bottom-20px'
                            label={<FormattedMessage
                                description='Label text'
                                defaultMessage='Event Name'
                                id='oe.common.eventName'
                            />}
                        >
                            <input
                                className='oe-form-field__control'
                                value={formData.name}
                                onChange={mobx.action(e => formData.name = e.target.value)}
                                onBlur={mobx.action(({ target: { value } }) => removeOnlySpacesFromField(value, 'name'))}
                            />
                            {formData.isValidationErrorExist() && <span className='oe-form-field__error'>{formData.getNameErrorMessage()}</span>}
                        </FormField>
                        {isEdit &&
                            <>
                                <FormField className='m-bottom-20px'>
                                    <div className='flex center'>
                                        <OEToggle
                                            isSelected={formData.showEventTitleForPassport}
                                            onClick={formData.toggleCustomMeetingTitleForPassport}
                                        />
                                        <Text
                                            message='Custom Event Title for Passport is {suffix}'
                                            id='oe.event.customEventTitleForPassportToggle'
                                            description='Label text'
                                            values={{ suffix: formData.showEventTitleForPassport ? EnablementStatus.ENABLED : EnablementStatus.DISABLED }}
                                        />
                                    </div>
                                </FormField>
                                {formData.showEventTitleForPassport &&
                                    <FormField
                                        className='m-bottom-20px'
                                        label={
                                            <FormattedMessage {...EVENT_TITLE_FOR_PASSPORT_TEXT} />
                                        }
                                    >
                                        <textarea
                                            rows={2}
                                            className='oe-form-field__control passport-form__textarea'
                                            value={formData.eventTitleForPassport}
                                            onChange={({ target: { value } }) => formData.setEventTitleForPassport(value)}
                                            onBlur={mobx.action(({ target: { value } }) => removeOnlySpacesFromField(value, 'eventTitleForPassport'))}
                                        />
                                        {formData.getEventTitleForPassportError() && <span className='oe-form-field__error'>{formData.getEventTitleForPassportError()}</span>}
                                    </FormField>
                                }
                                <FormField className='m-bottom-30px'>
                                    <EventProgressBar
                                        status={formData.eventStatus}
                                        changeStatus={mobx.action(value => formData.eventStatus = value)}
                                        disabled={isEventCancelled}
                                    />
                                </FormField>
                            </>
                        }
                        <div className='event_date_field m-bottom-25px'>
                            <div className='oe-form__row'>
                                <FormField
                                    label={<FormattedMessage {...START_DATE_TEXT} />}
                                >
                                    <DefaultDatePicker
                                        className='oe-form-field__control'
                                        selected={formData.getStartDate()}
                                        onChange={date => formData.setStartDate(date)}
                                        popperPlacement='bottom-start'
                                        showTimeSelect={false}
                                        dateformat={dateFormat}
                                    />
                                    {formData.showErrors() &&
                                        <span className='oe-form-field__error'>{formData.getStartDateError()}</span>}
                                </FormField>
                                <FormField
                                    label={<FormattedMessage {...END_DATE_TEXT} />}
                                >
                                    <DefaultDatePicker
                                        className='oe-form-field__control'
                                        selected={formData.getEndDate()}
                                        onChange={date => formData.setEndDate(date)}
                                        popperPlacement='bottom-start'
                                        showTimeSelect={false}
                                        dateformat={dateFormat}
                                    />
                                    {formData.showErrors() &&
                                        <span className='oe-form-field__error'>{formData.getEndDateError()}</span>}
                                </FormField>
                            </div>
                            {formData.showValidateErrors() && <span className='oe-form-field__error'>{formData.getDatesError()}</span>}
                        </div>
                        <div className='event_time_field m-bottom-25px'>
                            <div className='oe-form__row'>
                                <FormField
                                    label={<FormattedMessage {...START_TIME_TEXT} />}
                                >
                                    <TimeInput
                                        className='oe-form-field__control'
                                        date={formData.startDateTime}
                                        disabled={!formData.startDate}
                                        onChange={date => formData.setStartTime(date)}
                                        onError={mobx.action(() => formData.startTimeValidationError = true)}
                                    />
                                    {formData.showErrors() &&
                                        <span className='oe-form-field__error'>{formData.getStartTimeError()}</span>}
                                </FormField>
                                <FormField label={<FormattedMessage{...END_TIME_TEXT} />}>
                                    <TimeInput
                                        className='oe-form-field__control'
                                        date={formData.endDateTime}
                                        disabled={!formData.endDate}
                                        onChange={date => formData.setEndTime(date)}
                                        onError={mobx.action(() => formData.endTimeValidationError = true)}
                                    />
                                    {formData.showErrors() &&
                                        <span className='oe-form-field__error'>{formData.getEndTimeError()}</span>}
                                </FormField>
                            </div>
                            {formData.showValidateErrors() && <span className='oe-form-field__error'>{formData.getTimesError()}</span>}
                        </div>
                        <div className='event_staffing_date_field m-bottom-25px'>
                            <div className='oe-form__row'>
                                <FormField
                                    label={<FormattedMessage {...STAFFING_START_DATE_TEXT} />}
                                >
                                    <DefaultDatePicker
                                        className='oe-form-field__control'
                                        selected={formData.getStaffingStartDate()}
                                        onChange={date => formData.setStaffingStartDate(date)}
                                        popperPlacement='bottom-start'
                                        showTimeSelect={false}
                                        isClearable={true}
                                        dateformat={dateFormat}
                                    />
                                </FormField>
                                <FormField
                                    label={<FormattedMessage {...STAFFING_END_DATE_TEXT} />}
                                >
                                    <DefaultDatePicker
                                        className='oe-form-field__control'
                                        selected={formData.getStaffingEndDate()}
                                        onChange={date => formData.setStaffingEndDate(date)}
                                        popperPlacement='bottom-start'
                                        showTimeSelect={false}
                                        isClearable={true}
                                        dateformat={dateFormat}
                                    />
                                </FormField>
                            </div>
                            {formData.showValidateErrors() && <span className='oe-form-field__error'>{formData.getStaffingDatesError()}</span>}
                        </div>
                        {
                            formData.blueprintsAvailable &&
                            <FormField
                                label={<FormattedMessage
                                    description='Label text'
                                    defaultMessage='Event Blueprint'
                                    id='oe.blueprints.eventBlueprint'
                                />}
                            >
                                <select
                                    id='blueprint-selector'
                                    className='oe-form-field__control'
                                    value={formData.getSelectedBlueprintId()}
                                    onChange={e => formData.selectBlueprint(e.target.value)}
                                    disabled={formData.isLoadingBlueprints}
                                >
                                    <option value={''}>
                                        {intl.formatMessage({
                                            id: `oe.blueprints.noBlueprintSelected`,
                                            description: 'Label text',
                                            defaultMessage: 'No Blueprint Selected',
                                        })}
                                    </option>
                                    {
                                        formData.getBlueprintsOptions().map(({ blueprintId, name }) => {
                                            return <option value={blueprintId} key={blueprintId}>{name}</option>;
                                        })
                                    }
                                </select>
                                <ViewBlueprintDetailsSection
                                    blueprintDetails={formData.getSelectedBlueprintDetails()}
                                    blueprintItemType={formData.getSelectedBlueprintType()}
                                />
                            </FormField>
                        }
                        {
                            formData.showMeetingTopicField() &&
                            <FormField
                                label={<FormattedMessage
                                    description='Label text'
                                    defaultMessage='Meeting Topic'
                                    id='oe.blueprints.meetingTopic'
                                />}
                            >
                                <input
                                    id='meeting-topic-input'
                                    className='oe-form-field__control'
                                    value={formData.meetingTopic}
                                    onChange={e => formData.setMeetingTopic(e.target.value)}
                                />
                                {formData.isValidationErrorExist() && <span className='oe-form-field__error'>{formData.getMeetingTopicErrorMessage()}</span>}
                            </FormField>
                        }
                        <FormField
                            label={<FormattedMessage
                                description='Label text'
                                defaultMessage='Time Zone'
                                id='oe.common.timeZone'
                            />}
                        >
                            <SelectTimezones
                                value={formData.getTimeZone()}
                                onChange={mobx.action(timeZone => {
                                    formData.timeZone = timeZone;
                                })}
                                className='oe-form-field__control'
                                placeHolder={
                                    intl.formatMessage({
                                        id: `oe.timezone.selectTimezone`,
                                        description: 'Placeholder text',
                                        defaultMessage: 'Select timezone',
                                    })
                                }
                            />
                            {formData.showErrors() && <span className='oe-form-field__error'>{formData.getTimeZoneError()}</span>}
                        </FormField>
                        <FormField
                            label={<FormattedMessage
                                description='Label text'
                                defaultMessage='Region'
                                id='oe.event.region'
                            />}
                        >
                            <select
                                id='region-selector'
                                className='oe-form-field__control'
                                value={formData.getRegion()}
                                onChange={mobx.action(e => {
                                    formData.region = e.target.value;
                                })}
                            >
                                <option value={''}>
                                    {intl.formatMessage({
                                        id: `oe.event.noRegionSelected`,
                                        description: 'Label text',
                                        defaultMessage: 'No Region Selected',
                                    })}
                                </option>
                                {
                                    eventRegions(intl).map(({ label }, i) => {
                                        return <option value={label} key={i}>{label}</option>;
                                    })
                                }
                            </select>
                        </FormField>
                        <FormField
                            label={<FormattedMessage
                                description='Label text'
                                defaultMessage='Business Line'
                                id='oe.event.businessLine'
                            />}
                        >
                            <select
                                id='businessLine-selector'
                                className='oe-form-field__control'
                                value={formData.businessLine}
                                onChange={mobx.action(e => {
                                    formData.businessLine = e.target.value;
                                })}
                            >
                                <option value={''}>
                                    {intl.formatMessage({
                                        id: `oe.event.noBusinessLineSelected`,
                                        description: 'Label text',
                                        defaultMessage: 'No Business Line Selected',
                                    })}
                                </option>
                                {
                                    eventBusinessLines(intl).map(({ label }, i) => {
                                        return <option value={label} key={i}>{label}</option>;
                                    })
                                }
                            </select>
                        </FormField>
                        <FormField
                            label={<FormattedMessage
                                description='Label text'
                                defaultMessage='Event Type'
                                id='oe.event.eventType'
                            />}
                        >
                            <select
                                id='eventType-selector'
                                className='oe-form-field__control'
                                value={formData.getType()}
                                onChange={mobx.action(e => {
                                    formData.type = e.target.value;
                                })}
                            >
                                <option value={''} disabled hidden>
                                    {intl.formatMessage({
                                        id: `oe.event.selectEventType`,
                                        description: 'Label text',
                                        defaultMessage: 'Select Event Type',
                                    })}
                                </option>
                                {
                                    eventTypesLabel.map(({ label }, i) => {
                                        return <option value={label} key={i}>{label}</option>;
                                    })
                                }
                            </select>
                            {formData.showErrors() && <span className='oe-form-field__error'>{formData.getTypeError()}</span>}
                        </FormField>
                        {formData.isClientDepartmentLocked && (
                            <p className='event_form__billing-locked-notice' data-test-id='event-form-department-locked-notice'>
                                {intl.formatMessage(BILLING_LOCKED_NOTICE_TEXT)}
                            </p>
                        )}
                        <FormField
                            label={<FormattedMessage {...clientDepartmentText} />}
                        >
                            <select
                                id='clientDepartment-selector'
                                className='oe-form-field__control'
                                value={formData.getClientDepartment()}
                                disabled={formData.isClientDepartmentLocked}
                                onChange={mobx.action(e => {
                                    formData.department = e.target.value;
                                })}
                            >
                                <option value={''}>
                                    {intl.formatMessage(noClientDepartmentText)}
                                </option>
                                {
                                    formData.getDepartmentsOptions().map(({ label }, i) => {
                                        return <option value={label} key={i}>{label}</option>;
                                    })
                                }
                            </select>
                        </FormField>
                        <FormField
                            label={<FormattedMessage
                                description='Label text'
                                defaultMessage='Event Access'
                                id='oe.event.eventAccess'
                            />}
                        >
                            <select
                                id='eventAccess-selector'
                                className='oe-form-field__control'
                                value={formData.getEventAccess()}
                                onChange={e => { formData.setEventAccess(e.target.value as EventAccess); }}
                            >
                                <option value={''} disabled hidden>
                                    {intl.formatMessage({
                                        id: 'oe.event.selectEventAccess',
                                        description: 'Label text',
                                        defaultMessage: 'Select Event Access',
                                    })}
                                </option>
                                {
                                    eventAccess.map(({ value, label }, i) => {
                                        return <option value={value} key={`${i}_${value}`}>{label}</option>;
                                    })
                                }
                            </select>
                        </FormField>
                        <FormField
                            label={
                                <FormattedMessage
                                    description='Label text'
                                    defaultMessage='OE Number'
                                    id='oe.common.OENumber'
                                />}
                        >
                            <input
                                className='oe-form-field__control'
                                value={formData.oeNumber}
                                onChange={mobx.action(e => formData.oeNumber = e.target.value)}
                                onBlur={mobx.action(({ target: { value } }) => removeOnlySpacesFromField(value, 'oeNumber'))}
                            />
                            {formData.isValidationErrorExist() && <span className='oe-form-field__error'>{formData.getOeNumberErrorMessage()}</span>}
                        </FormField>
                        {isInternalEventLinksPermission && <FormField
                            label={
                                <FormattedMessage
                                    description='Label text'
                                    defaultMessage='Order ID'
                                    id='oe.common.orderId'
                                />}
                        >
                            <input
                                className='oe-form-field__control'
                                value={formData.orderId}
                                onChange={mobx.action(e => {
                                    formData.refreshOrderIdError();
                                    formData.orderId = e.target.value;
                                })}
                                onBlur={mobx.action(({ target: { value } }) => removeOnlySpacesFromField(value, 'orderId'))}
                            />
                            {formData.isValidationErrorExist() && <span className='oe-form-field__error'>{formData.getOrderIdErrorMessage()}</span>}
                        </FormField>}
                        <FormField
                            label={<FormattedMessage {...EXTERNAL_ID_TEXT} />}
                        >
                            <input
                                className='oe-form-field__control'
                                value={formData.externalId}
                                onChange={mobx.action(e => {
                                    formData.refreshExternalIdError();
                                    formData.externalId = e.target.value;
                                })}
                                onBlur={mobx.action(({ target: { value } }) => removeOnlySpacesFromField(value, 'externalId'))}
                            />
                            {formData.isValidationErrorExist() && <span className='oe-form-field__error'>{formData.getExternalIdErrorMessage()}</span>}
                        </FormField>
                        {isInternalEventLinksPermission && <FormField
                            label={
                                <FormattedMessage
                                    description='Label text'
                                    defaultMessage='Guidelines Link'
                                    id='oe.link.guidelinesLink'
                                />}
                        >
                            <input
                                className='oe-form-field__control'
                                value={formData.guidelinesLink}
                                onChange={mobx.action(e => formData.guidelinesLink = e.target.value)}
                                onBlur={mobx.action(({ target: { value } }) => removeOnlySpacesFromField(value, 'guidelinesLink'))}
                            />
                            {formData.isValidationErrorExist() && <span className='oe-form-field__error'>{formData.getGuidelinesLinkErrorMessage()}</span>}
                        </FormField>}
                        {isInternalEventLinksPermission && <FormField
                            label={
                                <FormattedMessage
                                    description='Label text'
                                    defaultMessage='Project Plan Link'
                                    id='oe.link.projectPlanLink'
                                />}
                        >
                            <input
                                className='oe-form-field__control'
                                value={formData.projectPlanLink}
                                onChange={mobx.action(e => formData.projectPlanLink = e.target.value)}
                                onBlur={mobx.action(({ target: { value } }) => removeOnlySpacesFromField(value, 'projectPlanLink'))}
                            />
                            {formData.isValidationErrorExist() && <span className='oe-form-field__error'>{formData.getProjectPlanLinkErrorMessage()}</span>}
                        </FormField>}
                        <FormField
                            label={
                                <FormattedMessage
                                    description='Label text'
                                    defaultMessage='Default Meeting Duration (minutes)'
                                    id='oe.link.defaultMeetingDuration'
                                />}
                        >
                            <input
                                className='oe-form-field__control'
                                type='number'
                                min={0}
                                max={1440}
                                step={10}
                                value={formData.defaultMeetingDuration}
                                onChange={e => formData.onChangeDefaultDuration(e)}
                            />
                            {formData.isValidationErrorExist()
                                && <span className='oe-form-field__error'>{formData.getDefaultMeetingDurationErrorMessage()}</span>}
                        </FormField>
                        {vm.getEventAttributes().length > 0 && <div className='event_form__attributes-section oe-form-field'>
                            <FormField
                                label={
                                    <FormattedMessage
                                        description='Label text'
                                        defaultMessage='Event Attributes'
                                        id='oe.link.eventAttributes'
                                    />
                                }
                                className='attribute-items'
                            >
                                {vm.getEventAttributes().map(attribute => {
                                    return (
                                        <div key={attribute.eventAttributeId}>
                                            {attribute.inputType === EventAttributeTypesValues.SINGLE_SELECT_DROPDOWN
                                                && attribute.values.filter(v => v.active).length > 0 && (
                                                    <FormField
                                                        label={attribute.eventAttributeName}
                                                    >
                                                        <select
                                                            id={`attribute-selector-${attribute.eventAttributeId}`}
                                                            className='oe-form-field__control'
                                                            value={formData.getAttributeValue(attribute.eventAttributeId) as string || ''}
                                                            onChange={mobx.action(e => {
                                                                formData.setAttributeValue(attribute.eventAttributeId, e.target.value);
                                                            })}
                                                        >
                                                            <option value=''>Select</option>
                                                            {
                                                                attribute.values.filter(v => v.active)
                                                                .sort((a, b) => a.attributeOrder - b.attributeOrder)
                                                                .map(value => {
                                                                    return <option value={value.valueId} key={value.valueId}>{value.label}</option>;
                                                                })
                                                            }
                                                        </select>
                                                    </FormField>
                                                )}
                                            {attribute.inputType === EventAttributeTypesValues.CHECKBOX && (
                                                <OECheckbox
                                                    checked={formData.getAttributeValue(attribute.eventAttributeId, true) as boolean}
                                                    onChange={() => formData.setAttributeValue(
                                                        attribute.eventAttributeId,
                                                        !formData.getAttributeValue(attribute.eventAttributeId, true)
                                                    )}
                                                    label={<Text message={attribute.eventAttributeName} withEllipsis />}
                                                />
                                            )}
                                            {attribute.inputType === EventAttributeTypesValues.FREE_TEXT && (
                                                <FormField
                                                    label={attribute.eventAttributeName}
                                                >
                                                    <input
                                                        id={`attribute-freetext-${attribute.eventAttributeId}`}
                                                        className='oe-form-field__control'
                                                        value={formData.getAttributeValue(attribute.eventAttributeId) as string || ''}
                                                        onChange={mobx.action(e => {
                                                            formData.setAttributeValue(attribute.eventAttributeId, e.target.value);
                                                        })}
                                                    />
                                                    {formData.getFreeTextAttributeError(attribute.eventAttributeId) &&
                                                        <span className='oe-form-field__error'>
                                                            {formData.getFreeTextAttributeError(attribute.eventAttributeId)}
                                                        </span>
                                                    }
                                                </FormField>
                                            )}
                                            {attribute.inputType === EventAttributeTypesValues.PARAGRAPH && (
                                                <FormField
                                                    label={attribute.eventAttributeName}
                                                >
                                                    <textarea
                                                        id={`attribute-paragraph-${attribute.eventAttributeId}`}
                                                        className='oe-form-field__control'
                                                        rows={4}
                                                        value={formData.getAttributeValue(attribute.eventAttributeId) as string || ''}
                                                        onChange={mobx.action(e => {
                                                            formData.setAttributeValue(attribute.eventAttributeId, e.target.value);
                                                        })}
                                                    />
                                                    {formData.getParagraphAttributeError(attribute.eventAttributeId) &&
                                                        <span className='oe-form-field__error'>
                                                            {formData.getParagraphAttributeError(attribute.eventAttributeId)}
                                                        </span>
                                                    }
                                                </FormField>
                                            )}
                                            {attribute.inputType === EventAttributeTypesValues.MULTI_SELECT_DROPDOWN
                                              && attribute.values && attribute.values.filter(v => v.active).length > 0 && (
                                                <div className='event_form__multi-attributes-section'>
                                                    <div className='event_form__multi-attributes-section__title'>
                                                        {attribute.eventAttributeName}
                                                    </div>
                                                    {formData.getSelectedAttributeValues(attribute).length > 0 && <div className='event_form__multi-attributes-section__selected-items'>
                                                        {(() => {
                                                            return formData.getSelectedAttributeValues(attribute)
                                                                .slice()
                                                                .sort((a, b) => a.label.localeCompare(b.label))
                                                                .map(({ valueId, label }) => (
                                                                    <div key={`${attribute.eventAttributeId}-${valueId}`} className='event_form__multi-attributes-section__item-wrapper'>
                                                                        <Text
                                                                            className='event_form__items-item-label'
                                                                            message={label}
                                                                            withEllipsis
                                                                        />
                                                                        <DefaultButton
                                                                            text={<FormattedMessage {...REMOVE_TEXT} />}
                                                                            modifier='remove'
                                                                            onClick={() =>
                                                                                formData.removeMultiSelectAttributeValue(attribute.eventAttributeId, valueId)
                                                                            }
                                                                            className='event_form__multi-attributes-section__remove-item-button'
                                                                        />
                                                                    </div>
                                                                ));
                                                        })()}
                                                    </div>}
                                                    <div className='event_form__multi-attributes-section__attribute-block'>
                                                        {(() => {
                                                            const selectedItems = formData.getSelectedAttributeValues(attribute);
                                                            const items = attribute.values.filter(v => v.active)
                                                                .sort((a, b) => a.attributeOrder - b.attributeOrder)
                                                                .map(v => ({ valueId: String(v.valueId), label: v.label }));

                                                            return (
                                                                <MultiSelectButton
                                                                    className='event_form__multi-attributes-section__add-button'
                                                                    items={items}
                                                                    value={selectedItems}
                                                                    keyProperty='valueId'
                                                                    nameProperty='label'
                                                                    text={
                                                                        <FormattedMessage
                                                                            id='oe.eventAttributes.addAttribute'
                                                                            description='Button text'
                                                                            defaultMessage='{prefix} Add {attributeName}'
                                                                            values={{
                                                                                prefix: '+',
                                                                                attributeName: attribute.eventAttributeName
                                                                            }}
                                                                        />
                                                                    }
                                                                    applyButtonText={addText()}
                                                                    resetButtonText={cancelText()}
                                                                    minSelectionRequired={1}
                                                                    validationErrorMessage={intl.formatMessage({...MIN_VALUE_REQUIRED_ERROR_TEXT})}
                                                                    onChange={mobx.action(multiSelectedItems => {
                                                                        const multiSelectedValueIds = multiSelectedItems.map(item => item.valueId);
                                                                        formData.setMultiSelectAttributeValues(
                                                                            attribute.eventAttributeId, multiSelectedValueIds
                                                                        );
                                                                    })}
                                                                />
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </FormField>
                        </div>}
                        <div className='event_form__items-section'>
                            <div className='event_form__items-section__title'>
                                <FormattedMessage {...COORDINATORS_TEXT} />
                            </div>
                            <div className='event_form__items-section__selected-items'>
                                {!formData.assignedCoordinators.length ?
                                    <div className='event_form__items-section__message'>
                                        <FormattedMessage
                                            id='oe.event.noCoordinators'
                                            description='Placeholder text'
                                            defaultMessage='Event has no assigned coordinators'
                                        />
                                    </div> :
                                    formData.assignedCoordinators
                                        .slice()
                                        .sort((a, b) => a.firstName.localeCompare(b.firstName))
                                        .map(({ username, firstName, lastName }) => (
                                            <div key={username} className='event_form__items-section__item-wrapper'>
                                                <Text
                                                    className='event_form__items-item-label'
                                                    message={`${firstName} ${lastName}`}
                                                    withEllipsis
                                                />
                                                <DefaultButton
                                                    text={<FormattedMessage {...REMOVE_TEXT} />}
                                                    modifier='remove'
                                                    onClick={() => formData.removeCoordinator(username)}
                                                    className='event_form__items-section__remove-item-button'
                                                />
                                            </div>
                                        ))}
                            </div>
                            <div className='event_form__items-section__coordinators-block'>
                                <MultiSelectButton
                                    className='event_form__items-section__add-button'
                                    items={formData.selectableCoordinators}
                                    disabled={formData.assignedCoordinators}
                                    value={formData.assignedCoordinators}
                                    keyProperty='username'
                                    nameProperty='name'
                                    filterHandler={formData.searchCoordinators}
                                    text={
                                        <FormattedMessage
                                            id='oe.coordinators.add'
                                            description='Button text'
                                            defaultMessage='{prefix}Add Coordinators'
                                            values={{
                                                prefix: '+ '
                                            }}
                                        />
                                    }
                                    applyButtonText={addText()}
                                    resetButtonText={cancelText()}
                                    onChange={formData.setAssignedCoordinators}
                                    onClick={formData.fetchCoordinatorsList}
                                />
                                <MultiSelectButton
                                    className='event_form__items-section__add-button'
                                    items={formData.selectableUserGroups}
                                    disabled={formData.assignedUserGroups}
                                    value={formData.assignedUserGroups}
                                    keyProperty='userGroupId'
                                    nameProperty='name'
                                    text={
                                        <FormattedMessage
                                            id='oe.userGroup.add'
                                            description='Button text'
                                            defaultMessage='{prefix}Add User Group'
                                            values={{
                                                prefix: '+ '
                                            }}
                                        />
                                    }
                                    applyButtonText={addText()}
                                    resetButtonText={cancelText()}
                                    onChange={formData.assignCoordinatorsFromGroupsList}
                                    onClick={formData.fetchUserGroupsList}
                                />
                            </div>
                        </div>
                        <DeliveryTeam
                            fetchDeliveryTeamItems={formData.fetchDeliveryTeamItems}
                            removeDeliveryItem={formData.removeDeliveryTeamItem}
                            setAssignedDeliveryTeam={formData.setAssignedDeliveryTeam}
                            searchDeliveryTeamItems={formData.searchDeliveryTeamItems}
                            assignedDeliveryTeamItems={formData.assignedDeliveryTeamItems}
                            selectableDeliveryTeamItems={formData.selectableDeliveryTeamItems}
                            deliveryMemberRoles={formattedDeliveryMemberRoles}
                            setDeliveryMemberRole={formData.setDeliveryMemberRole.bind(formData)}
                            intl={intl}
                        />
                        {hasPermission(Permission.VIEW_INTERNAL_EVENT_LINKS) && isEdit &&
                            <EventLinks
                                links={formData.links}
                                eventId={vm.getEventId()}
                                accountId={vm.getAccountId()}
                            />
                        }
                        <FormField
                            label={
                                <FormattedMessage {...EVENT_PUBLIC_DESCRIPTION_TEXT} />}
                            labelFor='event-public-desc'
                        >
                            <RichTextEditor
                                id='event-public-desc'
                                className='m-p-reset'
                                resizable
                                value={formData.publicDescription}
                                onChange={mobx.action((event, editor) => {
                                    formData.publicDescription = editor.getData();
                                })}
                                validationError={formData.showValidateErrors() && formData.getPublicDescriptionError()}
                            />
                        </FormField>
                        <FormField
                            label={
                                <FormattedMessage {...EVENT_PRIVATE_DESCRIPTION_TEXT} />}
                            labelFor='event-private-desc'
                        >
                            <RichTextEditor
                                id='event-private-desc'
                                className='m-p-reset'
                                resizable
                                value={formData.privateDescription}
                                onChange={mobx.action((event, editor) => {
                                    formData.privateDescription = editor.getData();
                                })}
                                validationError={formData.showValidateErrors() && formData.getPrivateDescriptionError()}
                            />
                        </FormField>
                        {isEdit &&
                            <UploadImageS3Presentation
                                vm={formData.uploadThumbnailImageS3VM}
                                dimensions={1000}
                                fieldName={intl.formatMessage(EVENT_THUMBNAIL_IMAGE_TEXT)}
                            />
                        }
                        {isEdit && hasPermission(Permission.PLANNING_NOTES) &&
                            <FormField
                                label={
                                    <FormattedMessage
                                        description='Label text'
                                        defaultMessage='Planning Notes'
                                        id='oe.event.planningNotes'
                                    />}
                                labelFor='event-planning-notes'
                            >
                                <RichTextEditor
                                    id='event-planning-notes'
                                    className='m-p-reset'
                                    value={formData.planningNotes}
                                    onChange={mobx.action((event, editor) => {
                                        formData.planningNotes = editor.getData();
                                    })}
                                    validationError={formData.showValidateErrors() && formData.getPlanningNotesError()}
                                />
                            </FormField>}
                        {hasPermission(Permission.PLANNING_NOTES) &&
                            <FormField
                                label={
                                    <FormattedMessage
                                        description='Label text'
                                        defaultMessage='Client Planning Notes'
                                        id='oe.event.clientPlanningNotes'
                                    />}
                                labelFor='event-client-planning-notes'
                            >
                                <RichTextEditor
                                    id='event-client-planning-notes'
                                    className='m-p-reset'
                                    value={formData.clientPlanningNotes}
                                    onChange={mobx.action((event, editor) => {
                                        formData.clientPlanningNotes = editor.getData();
                                    })}
                                    validationError={formData.showValidateErrors() && formData.getClientPlanningNotesError()}
                                />
                            </FormField>}
                        {isEdit && <EventRegistrationToken
                            createEventRegistrationToken={formData.createEventRegistrationToken}
                            generatingEventRegistrationToken={formData.generatingEventRegistrationToken}
                            thirdPartyRegistrationEnabled={formData.thirdPartyRegistrationEnabled}
                            eventRegistrationToken={formData.eventRegistrationToken}
                            intl={intl}
                        />}
                    </div>
                </form>
            )}
            footer={
                <>
                    {vm.isSaving() && <span className='oe-popup-layout__footer-status-area'>
                        <FormattedMessage
                            description='Placeholder text'
                            defaultMessage='Event is being created, please wait{suffix}'
                            id='oe.event.eventIsCreating'
                            values={{
                                suffix: '...'
                            }}
                        />
                    </span>}
                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultCloseSubWindowButton
                            text={submitText(isEdit)}
                            modifier={['fat', 'confirm-safe']}
                            disabled={isDisabled || vm.isSaving() || formData.isImageUploading()}
                            onClick={vm.submitForm} />
                        <DefaultCloseSubWindowButton />
                        {!!eventOptionsButton.length && <OptionsButton
                            className='options-button--right-margin-auto'
                            modifier={['fat', 'options']}
                            label={<FormattedMessage {...MORE_BUTTON_TEXT} />}
                            options={eventOptionsButton}
                            position='top'
                            align='start'
                        />}
                    </div>
                </>
            }
        />

    );
}));

export { EventFormPresentation };

